import { z } from "zod";
import { VERSION_FORMAT } from "./build.ts";
import {
  CLIENT_BUILD_ID,
  type ClientErrorEvent,
  clientContextSchema,
  clientErrorEventSchema,
  RECENT_ERRORS,
  reduceBuildId,
  reduceClientContext,
  reduceClientEvent,
  reduceRoute,
} from "./client-events.ts";
import { agentContextSchema, feedbackOriginSchema } from "./feedback-agent-context.ts";

/**
 * In-app feedback (openspec change `in-app-feedback`, spec `product-feedback`): what a user sends
 * to the ESI team. The dialog builds its preview from the object it sends, the API parses it with
 * this strict schema (any other field is a `400`) and reduces the route and errors again (design
 * D2), so the stored report holds only what the user saw, and never an identifier or content.
 */

export const FEEDBACK_PATH = "/api/feedback";
export const FEEDBACK_TYPES = ["bug", "idea"] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];
export const FEEDBACK_DESCRIPTION_MAX = 4_000;
/** Reports one user may send per rolling hour (`429` past it). */
export const FEEDBACK_PER_HOUR = 10;

/** A reporter's status: from the report's group (slice 3), `new` outside any group. */
export const FEEDBACK_STATUSES = [
  "new",
  "triaged",
  "planned",
  "fixed",
  "not_planned",
  "duplicate",
] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];
/** The statuses that close a group (retention counts from them, design D5). */
export const FEEDBACK_CLOSED_STATUSES = ["fixed", "not_planned", "duplicate"] as const;
/**
 * The statuses a reporter is notified of (feedback-reporter-status): every one but `new`, which a
 * group only returns to.
 */
export const FEEDBACK_NOTICE_STATUSES = [
  "triaged",
  "planned",
  "fixed",
  "not_planned",
  "duplicate",
] as const;
export type FeedbackNoticeStatus = (typeof FEEDBACK_NOTICE_STATUSES)[number];
/** The operator's "not planned" reason, shown to the group's reporters (feedback-reporter-status). */
export const FEEDBACK_REASON_MAX = 280;

/**
 * The automatic context, in the groups the dialog ticks or unticks as a whole: an unticked group
 * is absent. Nothing here can carry a URL, an identifier or content: closed enums, the build-ID
 * and version formats, a route name (a path is reduced to `:id`s) and reduced errors.
 */
export const feedbackContextSchema = z.strictObject({
  /** The SPA's build and release version, and when the user sent it (their clock). */
  app: z
    .strictObject({
      build: z.string().regex(CLIENT_BUILD_ID).optional(),
      version: z.string().max(40).regex(VERSION_FORMAT).optional(),
      time: z.iso.datetime().optional(),
    })
    .optional(),
  /** The router's route name (`conversation`, `project.files`), never the path or a title. */
  route: z.string().min(1).max(120).optional(),
  /** Coarse classes only, never the user agent. */
  device: clientContextSchema.optional(),
  /** The tab's last errors, reduced as client telemetry reduces them. */
  recentErrors: z.array(clientErrorEventSchema).max(RECENT_ERRORS).optional(),
});
export type FeedbackContext = z.infer<typeof feedbackContextSchema>;
/** The context groups, in the dialog's order. */
export const FEEDBACK_CONTEXT_GROUPS = ["app", "route", "device", "recentErrors"] as const;
export type FeedbackContextGroup = (typeof FEEDBACK_CONTEXT_GROUPS)[number];

export const sendFeedbackRequestSchema = z.strictObject({
  type: z.enum(FEEDBACK_TYPES),
  description: z.string().trim().min(1).max(FEEDBACK_DESCRIPTION_MAX),
  context: feedbackContextSchema,
});
export type SendFeedbackRequest = z.infer<typeof sendFeedbackRequestSchema>;

export const sendFeedbackResponseSchema = z.object({
  id: z.uuid(),
  createdAt: z.iso.datetime({ offset: true }),
});
export type SendFeedbackResponse = z.infer<typeof sendFeedbackResponseSchema>;

/**
 * The context with every field reduced (the SPA before showing the preview, the API before
 * storing): the route to a route name or a `:id` path, the locale to language and region, the
 * build to the build-ID format, each error as telemetry reduces it. `origin` is the page's.
 */
export function reduceFeedbackContext(context: FeedbackContext, origin?: string): FeedbackContext {
  const out: FeedbackContext = {};
  if (context.app) {
    const build = reduceBuildId(context.app.build);
    out.app = {
      ...(build !== undefined && { build }),
      ...(context.app.version !== undefined &&
        VERSION_FORMAT.test(context.app.version) && { version: context.app.version }),
      ...(context.app.time !== undefined && { time: context.app.time }),
    };
  }
  if (context.route !== undefined) {
    out.route = reduceRoute(context.route);
  }
  if (context.device) {
    out.device = reduceClientContext(context.device);
  }
  if (context.recentErrors) {
    out.recentErrors = context.recentErrors.map(
      (e) => reduceClientEvent(e, origin) as ClientErrorEvent,
    );
  }
  return out;
}

/**
 * Screenshots (design D4): opt-in per send, captured in the browser with every content region
 * masked unless the user unmasks it for that send, re-encoded as a PNG without metadata, and
 * uploaded after the report exists to `feedback/<tenantId>/<reportId>.png` through a presigned
 * PUT. The send body itself never changes, so an API without screenshots answers `404` to the
 * screenshot routes and the report stays sent without one.
 */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
export const FEEDBACK_SCREENSHOT_TYPE = "image/png";
/** A capture is at most a 4K screen at 2x: the confirm refuses anything larger. */
export const FEEDBACK_SCREENSHOT_MAX_SIDE = 8192;
export const FEEDBACK_SCREENSHOT_MAX_PIXELS = 40_000_000;
/** The bucket's lifecycle deletes screenshots after this many days (infra `documents.ts`). */
export const FEEDBACK_SCREENSHOT_DAYS = 90;
/** The window after a send in which its screenshot may be uploaded and confirmed. */
export const FEEDBACK_SCREENSHOT_WINDOW_SECONDS = 15 * 60;

export const feedbackScreenshotRequestSchema = z.strictObject({
  /** The PNG's exact size: the presigned PUT signs it. */
  sizeBytes: z.number().int().min(1).max(FEEDBACK_SCREENSHOT_MAX_BYTES),
});
export type FeedbackScreenshotRequest = z.infer<typeof feedbackScreenshotRequestSchema>;

export const feedbackScreenshotConfirmSchema = z.strictObject({
  /** Whether the user unmasked any content region for this send (`screenshot_unmasked`). */
  unmasked: z.boolean(),
});
export type FeedbackScreenshotConfirm = z.infer<typeof feedbackScreenshotConfirmSchema>;

export const feedbackScreenshotUploadSchema = z.object({
  url: z.string().min(1),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.iso.datetime({ offset: true }),
});
export type FeedbackScreenshotUpload = z.infer<typeof feedbackScreenshotUploadSchema>;

/** A minted read URL of a screenshot (at most 60 seconds). */
export const feedbackScreenshotLinkSchema = z.object({
  url: z.string().min(1),
  expiresAt: z.iso.datetime({ offset: true }),
});
export type FeedbackScreenshotLink = z.infer<typeof feedbackScreenshotLinkSchema>;

/** One of the caller's own reports ("My feedback"). */
export const myFeedbackItemSchema = z.object({
  id: z.uuid(),
  type: z.enum(FEEDBACK_TYPES),
  description: z.string(),
  status: z.enum(FEEDBACK_STATUSES),
  /** For `fixed`: the release that shipped the fix, when known (slice 3). */
  fixedIn: z.string().optional(),
  /** For `not_planned`: the operator's reason, when they gave one (feedback-reporter-status). */
  reason: z.string().optional(),
  /** A screenshot is stored and not yet expired (absent from an API without screenshots). */
  screenshot: z.boolean().optional(),
  createdAt: z.iso.datetime({ offset: true }),
});
export type MyFeedbackItem = z.infer<typeof myFeedbackItemSchema>;
export const myFeedbackResponseSchema = z.object({ reports: z.array(myFeedbackItemSchema) });
export type MyFeedbackResponse = z.infer<typeof myFeedbackResponseSchema>;

/** A row of the operators' Feedback inbox: no description, no reporter. */
export const operatorFeedbackItemSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  type: z.enum(FEEDBACK_TYPES),
  status: z.enum(FEEDBACK_STATUSES),
  build: z.string().nullable(),
  version: z.string().nullable(),
  route: z.string().nullable(),
  createdAt: z.iso.datetime({ offset: true }),
  /**
   * The report's group, its operator-written title only (feedback-inbox-followups D1); null in
   * no group. Absent from an older API, where the inbox shows no group column.
   */
  group: z.object({ id: z.uuid(), title: z.string() }).nullable().optional(),
  /** Where the report came from (feedback-from-conversation); absent from older APIs. */
  origin: feedbackOriginSchema.optional(),
  /**
   * The assistant's pending suggestion for an Unsorted report (feedback-triage-assist): the
   * suggested group's title, or a new group's; absent from an older API or when there is none.
   */
  suggestion: z.object({ title: z.string(), isNew: z.boolean() }).nullable().optional(),
});
export type OperatorFeedbackItem = z.infer<typeof operatorFeedbackItemSchema>;

/** One report as an operator opens it: the item, the description (plain text) and the context. */
export const operatorFeedbackReportSchema = operatorFeedbackItemSchema.extend({
  description: z.string(),
  context: feedbackContextSchema,
  /** The stored, unexpired screenshot and whether the reporter unmasked a region; else null. */
  screenshot: z.object({ unmasked: z.boolean() }).nullable().optional(),
  /** The fingerprints stored with the report at send (absent from an older API). */
  fingerprints: z.array(z.string()).optional(),
  /** The server-built agent context of a report sent from a draft; null otherwise. */
  agentContext: agentContextSchema.nullable().optional(),
});
export type OperatorFeedbackReport = z.infer<typeof operatorFeedbackReportSchema>;

export const FEEDBACK_INBOX_PAGE = 50;
/** The inbox's Group filter value for reports in no group ("Unsorted"). */
export const FEEDBACK_GROUP_NONE = "none";
export const operatorFeedbackQuerySchema = z.strictObject({
  status: z.enum(FEEDBACK_STATUSES).optional(),
  type: z.enum(FEEDBACK_TYPES).optional(),
  tenantId: z.uuid().optional(),
  build: z.string().regex(CLIENT_BUILD_ID).optional(),
  /** Reports in no group (`none`) or in one group (an unknown group lists nothing). */
  group: z.union([z.literal(FEEDBACK_GROUP_NONE), z.uuid()]).optional(),
  /** Keyset cursor: the last row's `id` (its time is read from the row); `before` alone, a time. */
  before: z.iso.datetime({ offset: true }).optional(),
  beforeId: z.uuid().optional(),
});
export type OperatorFeedbackQuery = z.infer<typeof operatorFeedbackQuerySchema>;
export const operatorFeedbackListSchema = z.object({
  reports: z.array(operatorFeedbackItemSchema),
  /** More rows past the last one. */
  more: z.boolean(),
});
export type OperatorFeedbackList = z.infer<typeof operatorFeedbackListSchema>;
